import { Provider } from "~/services";
import { useBreakpoint } from "~/shared";
import { TimeSlot } from "~/shared.types";
import { useSchedulingTimezoneStore } from "~/stores";
import { formatDateKey, tw } from "~/utils";
import {
  ContactInfo,
  NoProviderData,
  ProviderSchedulesSectionSkeleton,
} from ".";
import { useTimeZoneConversion } from "../hooks";
import {
  ProviderSchedule,
  type ProviderScheduleOptions,
} from "./ProviderSchedule";

type ProviderSchedulesSectionProps = {
  appointmentSlotsByProvider: Provider[];
  selectedDate: Date;
  selectedTimeSlot: TimeSlot | null;
  selectedProvider: number | null;
  onTimeSlotSelect: (timeSlot: TimeSlot, providerId?: number) => void;
  isPending?: boolean;
  scheduleOptions?: ProviderScheduleOptions;
  scrollable?: boolean;
};

export const ProviderSchedulesSection = ({
  appointmentSlotsByProvider,
  selectedDate,
  selectedTimeSlot,
  selectedProvider,
  onTimeSlotSelect,
  isPending = false,
  scheduleOptions,
  scrollable = true,
}: ProviderSchedulesSectionProps) => {
  const { selectedTimeZone } = useSchedulingTimezoneStore();
  const convertedProviders = useTimeZoneConversion(
    appointmentSlotsByProvider,
    selectedTimeZone,
    selectedDate,
  );
  const { isMobile } = useBreakpoint();

  // Filter converted providers to show only those with slots for the selected date
  const dateString = formatDateKey(selectedDate);

  const filteredConvertedProviders = convertedProviders.filter(
    (provider) =>
      provider.slots[dateString] && provider.slots[dateString].length > 0,
  );

  if (isPending) {
    return <ProviderSchedulesSectionSkeleton />;
  }

  if (!appointmentSlotsByProvider.length) {
    return <NoProviderData selectedDate={selectedDate} />;
  }

  return (
    <div className="space-y-4">
      <div
        className={tw(
          "space-y-2",
          scrollable && "overflow-y-auto pr-2 sm:max-h-96",
        )}
      >
        {filteredConvertedProviders.map((provider) => (
          <ProviderSchedule
            key={provider.id}
            provider={provider}
            selectedDate={selectedDate}
            selectedTimeSlot={
              selectedProvider === provider.id
                ? selectedTimeSlot || undefined
                : undefined
            }
            onTimeSlotSelect={(timeSlot) =>
              onTimeSlotSelect(timeSlot, provider.id)
            }
            {...scheduleOptions}
          />
        ))}
        {isMobile && <ContactInfo />}
      </div>
    </div>
  );
};
